import { Page } from "../page";
import { StoryClient } from "../signalr/story-client";
import { take } from "rxjs/operators";
import { ReadPage } from "./read-page";
import { ErrorTypes } from "../core/dtos";

class ReadPageInteractor extends Page {
  private client: StoryClient;
  private page: ReadPage;
  canGenerateMore: boolean;

  constructor() {
    super(/stories\/read\/.+/);
  }

  protected onLoaded(): void {
    this.client = new StoryClient(this.getCurrentStoryId());
    this.page = new ReadPage();

    if (this.page.storyIsNew()) {
      this.startStory();
    }

    this.page.onOptionButtonClicked((key) => this.pickChoice(key));
    this.page.onRegenerateButtonClicked(() => this.regenerateLastSection());

    this.handleErrors();
    this.listenForUserStatsChange();
  }

  private handleErrors() {
    this.client.errors$.subscribe((err) => {
      this.page.showError(err.message);
      this.page.hideLoadingSpinner();

      if (err.type != ErrorTypes.AlreadyGenerating) {
        this.page.showAllButtons();
      }
    });
  }

  private listenForUserStatsChange() {
    this.client.userStatsSubject$.subscribe((stats) => {
      this.canGenerateMore = stats.canGenerateMore;
      if (!stats.canGenerateMore) {
        this.page.disableAllButtons();
        this.page.showPromptLimitReachedPopover(true);
      } else {
        this.page.enableAllButtons();
        this.page.showPromptLimitReachedPopover(false);
      }
    });

    this.client.getUserStats();
  }

  private async regenerateLastSection(): Promise<void> {
    try {
      this.page.clearError();
      this.page.hideAllButtons();
      this.page.showLoadingSpinner();

      this.page.removeLastSection();

      this.setupStreamListeners();
      this.client.regenerateLastSection();
    } catch {}

    this.page.enableAllButtons();
  }

  private async pickChoice(choiceKey: number): Promise<void> {
    try {
      this.page.clearError();
      this.page.hideAllButtons();
      this.page.showLoadingSpinner();

      this.setupStreamListeners();
      this.client.continueWithChoice(choiceKey);
    } catch (e) {
      console.error(e);
    }
  }

  private async startStory(): Promise<void> {
    try {
      this.page.clearError();
      this.page.hideAllButtons();
      this.page.showLoadingSpinner();

      this.setupStreamListeners();
      this.client.startStory();
    } catch (e) {
      console.error(e);
    }
  }

  private getCurrentStoryId(): string {
    const url = new URL(location.href);
    return url.pathname.split("/").pop()!;
  }

  private setupStreamListeners() {
    let isInitialized = false;
    this.page.insertEmptySection();

    const textSub = this.client.text$.subscribe((txt) => {
      if (!isInitialized) {
        this.page.hideLoadingSpinner();
        this.page.focusLastSection();
        isInitialized = true;
      }

      const split = txt.split("\n\n");
      if (split.length === 1) {
        this.page.appendText(txt);
        return;
      }

      this.page.appendText(split[0]);
      this.page.appendNewParagraph(split[1]);
    });

    this.client.parsingOptions$.pipe(take(1)).subscribe((options) => {
      this.page.showLoadingSpinner();
    });

    this.client.sectionDone$.pipe(take(1)).subscribe((sectionDto) => {
      this.page.replaceLastSection(sectionDto);

      this.page.hideLoadingSpinner();
      this.page.showAllButtons();

      textSub.unsubscribe();
    });
  }
}

new ReadPageInteractor();
